/**
 * Admin Settings Page Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Account settings including visibility mode, branding, and regional settings.
 */

import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Info } from 'lucide-react';
import { settingsService } from '../../services/admin';
import type { AccountSettings, AccountSettingsUpdateData, ClientVisibilityMode } from '../../types/admin';
import { VISIBILITY_MODE_LABELS, VISIBILITY_MODE_DESCRIPTIONS } from '../../types/admin';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';

const VISIBILITY_MODES: ClientVisibilityMode[] = ['open', 'team_based', 'assigned_only', 'chinese_wall'];

const TIMEZONES = [
  'Europe/Zurich',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Singapore',
  'Asia/Hong_Kong',
];

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'German (Deutsch)' },
  { code: 'fr', name: 'French (Français)' },
  { code: 'it', name: 'Italian (Italiano)' },
];

const DATE_FORMATS = [
  { format: 'DD.MM.YYYY', example: '24.01.2026' },
  { format: 'MM/DD/YYYY', example: '01/24/2026' },
  { format: 'YYYY-MM-DD', example: '2026-01-24' },
];

const AdminSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AccountSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState<AccountSettingsUpdateData>({});

  // Load settings
  useEffect(() => {
    setIsLoading(true);
    settingsService
      .getAccountSettings()
      .then((data) => {
        setSettings(data);
        setFormData({
          client_visibility_mode: data.client_visibility_mode,
          company_name: data.company_name || '',
          default_timezone: data.default_timezone,
          default_language: data.default_language,
          date_format: data.date_format,
          enable_team_management: data.enable_team_management,
          enable_client_restrictions: data.enable_client_restrictions,
        });
      })
      .catch((err) => {
        console.error('Failed to load settings:', err);
        toast.error('Failed to load settings');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Track changes
  useEffect(() => {
    if (!settings) return;

    const changed =
      formData.client_visibility_mode !== settings.client_visibility_mode ||
      formData.company_name !== (settings.company_name || '') ||
      formData.default_timezone !== settings.default_timezone ||
      formData.default_language !== settings.default_language ||
      formData.date_format !== settings.date_format ||
      formData.enable_team_management !== settings.enable_team_management ||
      formData.enable_client_restrictions !== settings.enable_client_restrictions;

    setHasChanges(changed);
  }, [formData, settings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await settingsService.updateAccountSettings(formData);
      setSettings(updated);
      setHasChanges(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save settings'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (!settings) return;
    setFormData({
      client_visibility_mode: settings.client_visibility_mode,
      company_name: settings.company_name || '',
      default_timezone: settings.default_timezone,
      default_language: settings.default_language,
      date_format: settings.date_format,
      enable_team_management: settings.enable_team_management,
      enable_client_restrictions: settings.enable_client_restrictions,
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-500 mt-1">
            Configure account-wide settings and preferences
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleReset} disabled={!hasChanges || isSaving}>
            <RefreshCw size={16} className="mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            <Save size={16} className="mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Company Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={formData.company_name || ''}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
              placeholder="Your Company Name"
            />
          </div>
        </div>
      </div>

      {/* Client Visibility */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Client Visibility</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control how clients are visible to users in your organization
        </p>

        <div className="space-y-3">
          {VISIBILITY_MODES.map((mode) => (
            <label
              key={mode}
              className={`
                flex items-start gap-3 p-4 rounded-lg border cursor-pointer
                transition-colors
                ${formData.client_visibility_mode === mode
                  ? 'border-[#002B45] bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                name="visibility_mode"
                value={mode}
                checked={formData.client_visibility_mode === mode}
                onChange={() => setFormData({ ...formData, client_visibility_mode: mode })}
                className="mt-1 text-[#002B45] focus:ring-[#F7B500]"
              />
              <div>
                <span className="font-medium text-gray-900">{VISIBILITY_MODE_LABELS[mode]}</span>
                <p className="text-sm text-gray-500 mt-0.5">{VISIBILITY_MODE_DESCRIPTIONS[mode]}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Feature toggles based on visibility mode */}
        <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.enable_team_management || false}
              onChange={(e) => setFormData({ ...formData, enable_team_management: e.target.checked })}
              className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
            />
            <div>
              <span className="font-medium text-gray-900">Enable Team Management</span>
              <p className="text-sm text-gray-500">Allow users to be organized into teams</p>
            </div>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.enable_client_restrictions || false}
              onChange={(e) => setFormData({ ...formData, enable_client_restrictions: e.target.checked })}
              className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
            />
            <div>
              <span className="font-medium text-gray-900">Enable Client Restrictions</span>
              <p className="text-sm text-gray-500">Allow explicit allow/deny rules per client (Chinese walls)</p>
            </div>
          </label>
        </div>
      </div>

      {/* Regional Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Regional Settings</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Timezone
            </label>
            <select
              value={formData.default_timezone || 'Europe/Zurich'}
              onChange={(e) => setFormData({ ...formData, default_timezone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Language
            </label>
            <select
              value={formData.default_language || 'en'}
              onChange={(e) => setFormData({ ...formData, default_language: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Format
            </label>
            <select
              value={formData.date_format || 'DD.MM.YYYY'}
              onChange={(e) => setFormData({ ...formData, date_format: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            >
              {DATE_FORMATS.map((df) => (
                <option key={df.format} value={df.format}>
                  {df.format} ({df.example})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Changes to visibility mode may affect user access to clients immediately.
          Ensure your team is informed before making significant changes.
        </p>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
